import axios from 'axios';

import { PVSProvider } from '@/utils/availabilityUtils.js';
import { bookAppointment, processAPIWithHeaders } from '@/utils/helpers.js';
import { formatInsuranceType } from '@/utils/insurance-utils.ts';

import { useConfigStore } from '@/store.js';

const BASE_API_URL = `${import.meta.env.VITE_API_PROTOCOL}://${import.meta.env.VITE_API_DOMAIN}`;
const BASE_API_URL_FOR_PRACTITIONERS = `${import.meta.env.VITE_API_PROTOCOL}://${import.meta.env.VITE_PRAC_API_DOMAIN}`;

export async function getUserAppointments(usersession) {
    const userId = usersession?.idToken.payload.sub;
    const token = usersession?.idToken.jwtToken;
    try {
        const response = await axios.get(`${BASE_API_URL}/secure/user/appointments/?user_id=${userId}`, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: token,
            },
        });
        // console.debug("response", response)
        return response;
    } catch (error) {
        console.debug('error is', error);
        return [];
    }
}

export const getAppointment = async (userSession, appointmentId) => {
    const userId = userSession?.idToken.payload.sub;
    const token = userSession?.idToken.jwtToken;
    try {
        const response = await axios.get(
            `${BASE_API_URL}/secure/user/appointments/${appointmentId}?user_id=${userId}`,
            {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: token,
                },
            }
        );

        return response.data;
    } catch (e) {
        console.debug(e);
        return null;
    }
};

export async function cancelAppointmentForUser(
    usersession,
    appointmentId,
    userId,
    customerId,
    confirmCancellation = false
) {
    const token = usersession?.idToken.jwtToken;
    try {
        const customer = useConfigStore.getState().currentCustomer;
        const fallbackCustomerId = customer?.customer_id?.[0].text;
        const response = await processAPIWithHeaders(
            `secure/user/appointments/${
                appointmentId
            }/${confirmCancellation ? 'confirm-cancelled' : 'cancel'}?user_id=${userId}`,
            'POST',
            {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: token,
                    customer_id: customerId ?? fallbackCustomerId ?? '',
                },
            }
        );
        console.debug('response of cancel', response);
        return response;
    } catch (error) {
        console.debug('error is', error);
        return new Error(error.message);
    }
}

export async function getPractitionerForUser(usersession) {
    const userId = usersession.idToken.payload.sub;
    const token = usersession.idToken.jwtToken;

    try {
        const response = await axios.get(`${BASE_API_URL}/secure/user/appointments/professionals?user_id=${userId}`, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: token,
            },
        });
        return response;
    } catch (error) {
        return new Error(error.message);
    }
}

export async function getCalenderForUser(
    userId,
    insuranceTypeKey,
    isReturning,
    isOnline,
    startDate,
    treatmentsString,
    targetValue,
    targetType,
    customerId
) {
    console.debug('startDate : ', startDate, isReturning, isOnline, insuranceTypeKey, userId, treatmentsString);
    const customer = useConfigStore.getState().currentCustomer;
    const fallbackCustomerId = customer?.customer_id?.[0].text;

    const params = {
        appointment_type_keys: treatmentsString,
        insurance_type: formatInsuranceType(insuranceTypeKey),
        is_returning: isReturning,
        is_online: isOnline,
        start_date: startDate,
    };

    if (targetType === PVSProvider.DOC_CIRRUS) {
        params.dc_calendar_id = targetValue;
    } else if (targetType === PVSProvider.DATA_CUR) {
        params.data_cur_resource_id = targetValue;
    }

    try {
        const response = await axios.get(`${BASE_API_URL_FOR_PRACTITIONERS}/public/users/availability/${userId}`, {
            headers: {
                'Content-Type': 'application/json',
                customer_id: customerId ?? fallbackCustomerId ?? '',
            },
            params: params,
        });
        return response;
    } catch (error) {
        console.debug(`/availability/${userId}`, error);
        return [];
    }
}

export async function calenderUpdated(
    usersession,
    appointmentId,
    userId, // eterno user id
    isForSelf,
    isReturning,
    isPublicInsurance,
    isPrivateInsurance,
    isSelfPayer,
    isWithPrescription,
    document,
    symptom,
    additionalInfo,
    professionalId,
    professionalKey,
    professionalTypeKey,
    locationKey,
    types,
    dateSlot,
    durationValue,
    durationUnits,
    typeId,
    calendarId,
    practitionerId, // dc_practitioner_id
    isAtLocation,
    isOnline,
    customerId,
    pvsObj,
    isCancelledExternally = false
) {
    const token = usersession.idToken.jwtToken;
    console.debug('activeR', calendarId, document, symptom);

    try {
        const customer = useConfigStore.getState().currentCustomer;
        const fallbackCustomerId = customer?.customer_id?.[0].text;

        const body = {
            patient: {
                id: userId, // eterno user id
                is_eterno_user: true,
                is_for_self: isForSelf,
                is_returning: isReturning,
                is_public_insurance: isPublicInsurance,
                is_private_insurance: isPrivateInsurance,
                is_self_payer: isSelfPayer,
                documents: document,
                symptoms: symptom,
                is_with_prescription: isWithPrescription ?? false,
            },
            professional: {
                id: professionalId,
                key: professionalKey,
                type_key: professionalTypeKey,
                location_key: locationKey,
            },
            appointment: {
                id: appointmentId,
                types: types,
                slot: dateSlot,
                duration: {
                    value: durationValue,
                    units: durationUnits,
                },
                info: additionalInfo,
                is_at_location: isAtLocation,
                is_online: isOnline,
            },
            pvs: pvsObj,
        };

        if (isCancelledExternally) {
            const [isBooked, bookedAppointment, errorMessage] = await bookAppointment(userId, body, token);

            if (!isBooked) throw new Error(errorMessage);

            const cancelled = await cancelAppointmentForUser(usersession, appointmentId, userId, customerId, true);

            return { booked: bookedAppointment, cancelled: cancelled };
        }

        const response = await processAPIWithHeaders(
            `secure/user/appointments/${appointmentId}/reschedule?user_id=${userId}`,
            'POST',
            {
                body: body,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: token,
                    customer_id: customerId ?? fallbackCustomerId ?? '',
                },
            }
        );
        console.debug('response of reschedule', response);
        return response;
    } catch (error) {
        console.debug('error of reschedule', error);
        return [];
    }
}

export const getFeedback = async (userId, appointmentId) => {
    try {
        const response = await axios.get(
            `${BASE_API_URL}/public/user/appointments/feedback?user_id=${userId}&appointment_id=${appointmentId}`
        );
        return response.data.data;
    } catch (e) {
        console.debug('error fetching feedback', e);
        return undefined;
    }
};

export const postFeedback = async (userId, appointmentId, answers) => {
    try {
        await axios.post(`${BASE_API_URL}/public/user/appointments/feedback`, {
            user_id: userId,
            appointment_id: appointmentId,
            answers: answers,
        });
    } catch (e) {
        throw new Error(e);
    }
};
